import React, { useState, useRef, useEffect } from 'react'
import { gsap } from 'gsap'

interface Message {
  role: 'user' | 'assistant'
  content: string
}

const QUICK_REPLIES = [
  'What are your hours?',
  'How much does a haircut cost?',
  'Do you do bridal makeup?',
  'Where are you located?',
  'Book an appointment',
]

const KNOWLEDGE_BASE: Record<string, string> = {
  hours: 'We\'re open 10:00 AM – 1:00 AM, every day of the week. No exceptions — your grooming ritual fits your schedule.',
  price: 'Our services are bespoke. A consultation determines the exact investment. Hair couture starts at ₹3,500, makeup at ₹5,000, and skin rituals at ₹4,000. Bridal packages are custom-quoted.',
  bridal: 'Absolutely. Our Bridal Concierge handles everything — trials, day-of coordination, party looks, and even groom\'s grooming. We recommend booking 3–6 months ahead.',
  location: 'Aure Unisex Studio, Metro Pillar No. 544, Stadium Rd, Janatha, Palarivattom, Ernakulam, Kerala 682025. Easy to find — we\'re right by the metro pillar.',
  book: 'Perfect! Let\'s get you booked. Tap "Book an Appointment" below or tell me your preferred date, time, and service, and I\'ll open the reservation form for you.',
}

export default function AIChatbox() {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [typing, setTyping] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const chatRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (!chatRef.current) return

    const ctx = gsap.context(() => {
      gsap.fromTo(
        chatRef.current?.querySelector('.chatbox__panel'),
        { opacity: 0, y: 20, scale: 0.95 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.4,
          ease: 'back.out(1.7)',
        }
      )
    }, chatRef.current)

    return () => ctx.revert()
  }, [open])

  const addMessage = (role: 'user' | 'assistant', content: string) => {
    setMessages((prev) => [...prev, { role, content }])
  }

  const getBotResponse = (userMsg: string): string => {
    const lower = userMsg.toLowerCase()

    if (lower.includes('hour') || lower.includes('open') || lower.includes('time')) {
      return KNOWLEDGE_BASE.hours
    }
    if (lower.includes('price') || lower.includes('cost') || lower.includes('much') || lower.includes('rate')) {
      return KNOWLEDGE_BASE.price
    }
    if (lower.includes('bridal') || lower.includes('wedding') || lower.includes('marriage')) {
      return KNOWLEDGE_BASE.bridal
    }
    if (lower.includes('where') || lower.includes('location') || lower.includes('address') || lower.includes('find')) {
      return KNOWLEDGE_BASE.location
    }
    if (lower.includes('book') || lower.includes('appointment') || lower.includes('reserv') || lower.includes('schedule')) {
      return KNOWLEDGE_BASE.book
    }

    return `I\'d love to help with that! For detailed questions about our services, pricing, or to book an appointment, I can connect you with our concierge directly on WhatsApp. They personally confirm every booking. What would you like to know?`
  }

  const handleSend = (text?: string) => {
    const messageText = text || input.trim()
    if (!messageText) return

    const userMsg = messageText
    addMessage('user', userMsg)
    setInput('')
    setTyping(true)

    setTimeout(() => {
      const response = getBotResponse(userMsg)
      addMessage('assistant', response)
      setTyping(false)
    }, 800 + Math.random() * 600)
  }

  const handleQuickReply = (text: string) => {
    handleSend(text)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div ref={chatRef} className="chatbox">
      {!open && (
        <div className="chatbox__fab-wrap">
          <button
            className="chatbox__fab"
            onClick={() => setOpen(true)}
            aria-label="Open AI Assistant"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="chatbox__fab-icon">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span className="chatbox__fab-label">Ask AI</span>
          </button>
        </div>
      )}

      {open && (
        <div className="chatbox__panel" role="dialog" aria-label="Aure Studio AI Assistant">
          <div className="chatbox__glow" />
          <header className="chatbox__header">
            <div className="chatbox__avatar chatbox__avatar--lg">
              <span>A</span>
            </div>
            <div className="chatbox__info">
              <h4 className="chatbox__name">Aure AI</h4>
              <span className="chatbox__status">
                <span className="chatbox__status-dot" />
                Online · Replies in seconds
              </span>
            </div>
            <button
              className="chatbox__close"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </header>

          <div className="chatbox__messages">
            {messages.length === 0 && (
              <div className="chatbox__welcome">
                <div className="chatbox__msg chatbox__msg--assistant">
                  <div className="chatbox__msg-avatar">A</div>
                  <div className="chatbox__msg-bubble">
                    <p>Welcome to Aure Studio! I\'m your AI concierge. Ask me about our services, pricing, hours, location, or book an appointment — I\'m here to help.</p>
                    <div className="chatbox__quick-replies">
                      {QUICK_REPLIES.map((q) => (
                        <button
                          key={q}
                          className="chatbox__quick-btn"
                          onClick={() => handleQuickReply(q)}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`chatbox__msg chatbox__msg--${msg.role}`}>
                {msg.role === 'assistant' && <div className="chatbox__msg-avatar">A</div>}
                <div className="chatbox__msg-bubble">
                  <p>{msg.content}</p>
                  {msg.role === 'assistant' && i === messages.length - 1 && (
                    <div className="chatbox__quick-replies">
                      {QUICK_REPLIES.slice(0, 3).map((q) => (
                        <button
                          key={q}
                          className="chatbox__quick-btn"
                          onClick={() => handleQuickReply(q)}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {typing && (
              <div className="chatbox__msg chatbox__msg--assistant chatbox__msg--typing">
                <div className="chatbox__msg-avatar">A</div>
                <div className="chatbox__msg-bubble">
                  <div className="chatbox__typing">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <form className="chatbox__input" onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me anything…"
              aria-label="Message Aure AI"
              autoFocus
            />
            <button type="submit" disabled={!input.trim() || typing} aria-label="Send">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="20" height="។0">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            </button>
          </form>
        </div>
      )}
    </div>
  )
}